import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  CircularProgress,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api';


// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));



const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  borderRadius: 16,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: status === 'active' ? '#C6F6D5' : 
                   status === 'suspended' ? '#FED7D7' : 
                   status === 'invited' ? '#FEEBC8' : 
                   status === 'pending' ? '#E2E8F0' : '#FED7D7',
  color: status === 'active' ? '#22543D' : 
         status === 'suspended' ? '#742A2A' : 
         status === 'invited' ? '#744210' : 
         status === 'pending' ? '#4A5568' : '#742A2A',
}));

const RoleChip = styled(Chip)<{ role: string }>(({ theme, role }) => ({
  borderRadius: 12,
  fontSize: '0.7rem',
  fontWeight: 600,
  backgroundColor: role === 'super_admin' ? '#FED7D7' : 
                  role === 'admin' ? '#FEEBC8' : 
                  role === 'property_manager' ? '#C6F6D5' : 
                  role === 'owner' ? '#BEE3F8' : 
                  role === 'maintenance' ? '#FED7D7' : 
                  role === 'accounting' ? '#E9D8FD' : 
                  role === 'tenant' ? '#FEF5E7' : 
                  role === 'prospect' ? '#E6FFFA' : '#E2E8F0',
  color: role === 'super_admin' ? '#742A2A' : 
         role === 'admin' ? '#744210' : 
         role === 'property_manager' ? '#22543D' : 
         role === 'owner' ? '#2A4365' : 
         role === 'maintenance' ? '#742A2A' : 
         role === 'accounting' ? '#553C9A' : 
         role === 'tenant' ? '#744210' : 
         role === 'prospect' ? '#22543D' : '#4A5568',
}));

// Types
interface PropTrakUser {
  id: string;
  email: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  phone_number?: string;
  role: string;
  company_id: string;
  company_name?: string;
  created_at: string;
  last_login?: string;
  last_pm_portal_login?: string | null;
  status: string;
  system_disclosure: boolean;
  timezone?: string;
  is_primary_user?: boolean;
  is_deleted: boolean;
}

interface Company {
  id: string;
  company_name: string;
  subscription_plan: string;
  billing_status: string;
}

interface EnvironmentInfo {
  environment: string;
  dev_mode: boolean;
  proptrak_api_base: string;
  aletheia_database_configured: boolean;
  proptrak_database_configured: boolean;
}

const PropTrakUserManagement: React.FC = () => {
  // State
  const [users, setUsers] = useState<PropTrakUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [environment, setEnvironment] = useState<'staging' | 'production'>('staging');
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [operationLoading, setOperationLoading] = useState<{ [key: string]: boolean }>({});
  const [lastDataRefresh, setLastDataRefresh] = useState<Date | null>(null);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PropTrakUser | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; userId: string | null; userName: string }>({
    open: false,
    userId: null,
    userName: ''
  });
  
  // Form states
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    phone_number: '',
    password: '',
    role: 'property_manager',
    company_id: '',
    status: 'invited',
    system_disclosure: false,
    timezone: 'America/New_York',
  });

  // API base URL - use API service's environment detection
  const API_BASE = api.getEnvironment() === 'development' ? 'http://localhost:5001' :
                   api.getEnvironment() === 'staging' ? 'https://admin-staging.aletheiatech.ai' :
                   'https://admin.aletheiatech.ai';

  // Role options
  const roleOptions = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'property_manager', label: 'Property Manager' },
    { value: 'owner', label: 'Owner' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'accounting', label: 'Accounting' },
    { value: 'tenant', label: 'Tenant' },
    { value: 'prospect', label: 'Prospect' },
    { value: 'custom', label: 'Custom' },
  ];

  const statusOptions = [
    { value: 'invited', label: 'Invited (Recommended)' },
    { value: 'active', label: 'Active (Not Recommended)' },
    { value: 'suspended', label: 'Suspended' },
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Phoenix', label: 'Arizona Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'America/Anchorage', label: 'Alaska Time' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  ];

  // Load data
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('Loading PropTrak data from API_BASE:', API_BASE);
      
      const [usersData, companiesData, envData] = await Promise.all([
        api.get<PropTrakUser[]>('/api/proptrak/users'),
        api.get<Company[]>('/api/proptrak/companies'),
        api.get<EnvironmentInfo>('/api/admin/environment/')
      ]);
      
      setUsers(usersData || []);
      setCompanies(companiesData || []);
      setEnvironmentInfo(envData);
      
      // Update environment state based on backend response
      if (envData.environment && (envData.environment === 'staging' || envData.environment === 'production')) {
        setEnvironment(envData.environment);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data from server');
    } finally {
      setLoading(false);
      setLastDataRefresh(new Date());
    }
  };

  // User operations
  const handleCreateUser = async () => {
    // Validate required fields
    if (!userForm.email || !userForm.first_name || !userForm.last_name || !userForm.role || !userForm.company_id) {
      setError('Please fill in all required fields: Email, First Name, Last Name, Role, and Company');
      return;
    }

    // Validate password requirements based on status
    if (userForm.status === 'active' && !userForm.password) {
      setError('Password is required for active users');
      return;
    }

    // Show warning for active users
    if (userForm.status === 'active') {
      const confirmed = window.confirm(
        '⚠️ WARNING: Creating active users is not recommended for security reasons.\n\n' +
        'Active users can immediately log in with the password you provide, which poses security risks.\n\n' +
        'Recommended: Set status to "Invited" so users receive an email to set up their own password.\n\n' +
        'Do you want to continue creating an active user?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      const responseData = await api.post<{ user: PropTrakUser; message: string }>('/api/proptrak/users', userForm);
        
        // Add the new user to local state immediately for better UX
        if (responseData.user) {
          const newUser: PropTrakUser = {
            id: responseData.user.id,
            email: userForm.email,
            first_name: userForm.first_name,
            middle_name: userForm.middle_name,
            last_name: userForm.last_name,
            phone_number: userForm.phone_number,
            role: userForm.role,
            company_id: userForm.company_id,
            company_name: companies.find(c => c.id === userForm.company_id)?.company_name,
            created_at: new Date().toISOString(),
            last_login: undefined,
            last_pm_portal_login: undefined,
            status: userForm.status,
            system_disclosure: userForm.system_disclosure,
            timezone: userForm.timezone,
            is_deleted: false,
          };
          setUsers(prevUsers => [newUser, ...prevUsers]);
        }

      setSuccess('PropTrak user created successfully');
      setUserDialogOpen(false);
      resetUserForm();
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to create PropTrak user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    // Validate required fields
    if (!userForm.email || !userForm.first_name || !userForm.last_name || !userForm.role || !userForm.company_id) {
      setError('Please fill in all required fields: Email, First Name, Last Name, Role, and Company');
      return;
    }

    const operationKey = `update_${editingUser.id}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.put<{ message: string }>(`/api/proptrak/users/${editingUser.id}`, userForm);
      
      // Update the local state immediately for better UX
      const updatedUsers = users.map(user => {
        if (user.id === editingUser.id) {
          return {
            ...user,
            first_name: userForm.first_name,
            middle_name: userForm.middle_name,
            last_name: userForm.last_name,
            phone_number: userForm.phone_number,
            role: userForm.role,
            company_id: userForm.company_id,
            company_name: companies.find(c => c.id === userForm.company_id)?.company_name,
            status: userForm.status,
            system_disclosure: userForm.system_disclosure,
            timezone: userForm.timezone,
          };
        }
        return user;
      });
      setUsers(updatedUsers);

      setSuccess('PropTrak user updated successfully');
      setUserDialogOpen(false);
      setEditingUser(null);
      resetUserForm();
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to update PropTrak user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const operationKey = `delete_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.delete<{ message: string }>(`/api/proptrak/users/${userId}`);
      
      // Remove user from local state immediately
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      setSuccess('PropTrak user deleted successfully');
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete PropTrak user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const operationKey = `suspend_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.post<{ message: string }>(`/api/proptrak/users/${userId}/suspend`);
      
      // Update user status in local state immediately
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, status: 'suspended' } : user
      ));
      setSuccess('PropTrak user suspended successfully');
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error suspending user:', error);
      setError(error instanceof Error ? error.message : 'Failed to suspend PropTrak user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleActivateUser = async (userId: string) => {
    const operationKey = `activate_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.post<{ message: string }>(`/api/proptrak/users/${userId}/activate`);
      
      // Update user status in local state immediately
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, status: 'active' } : user
      ));
      setSuccess('PropTrak user activated successfully');
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error activating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to activate PropTrak user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleEnvironmentChange = async (newEnvironment: 'staging' | 'production') => {
    try {
      await api.post<{ message: string }>('/api/admin/switch-environment', { environment: newEnvironment });
      
      setEnvironment(newEnvironment);
      // Reload data after environment switch
      await loadData();
      setSuccess(`Switched to ${newEnvironment} environment`);
    } catch (error) {
      console.error('Error switching environment:', error);
      setError(error instanceof Error ? error.message : 'Failed to switch environment');
    }
  };

  // Form handlers
  const resetUserForm = () => {
    setUserForm({
      email: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      phone_number: '',
      password: '',
      role: 'property_manager',
      company_id: '',
      status: 'invited',
      system_disclosure: false,
      timezone: 'America/New_York',
    });
  };

  const openUserDialog = (user?: PropTrakUser) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        email: user.email,
        first_name: user.first_name,
        middle_name: user.middle_name || '',
        last_name: user.last_name,
        phone_number: user.phone_number || '',
        password: '',
        role: user.role,
        company_id: user.company_id,
        status: user.status,
        system_disclosure: user.system_disclosure,
        timezone: user.timezone || 'America/New_York',
      });
    } else {
      setEditingUser(null);
      resetUserForm();
    }
    setUserDialogOpen(true);
  };

  const openDeleteConfirmDialog = (userId: string, userName: string) => {
    setDeleteConfirmDialog({
      open: true,
      userId,
      userName
    });
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    resetUserForm();
  };

  // Render functions
  const renderUserDialog = () => (
    <Dialog open={userDialogOpen} onClose={closeUserDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingUser ? 'Edit PropTrak User' : 'Create New PropTrak User'}
      </DialogTitle>
      <DialogContent>
                 <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
           <Box sx={{ flex: 1 }}>
             <TextField
               fullWidth
               required
               label="Email *"
               value={userForm.email}
               onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
               disabled={!!editingUser}
             />
           </Box>
           <Box sx={{ flex: 1 }}>
             <TextField
               fullWidth
               label="Password"
               type="password"
               value={userForm.password}
               onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
               disabled={userForm.status === 'invited'}
               helperText={
                 editingUser 
                   ? 'Leave blank to keep current password' 
                   : userForm.status === 'active'
                   ? 'Password required for active users'
                   : ''
               }
             />
             {userForm.status === 'invited' && (
               <Typography 
                 variant="caption" 
                 color="text.secondary" 
                 sx={{ 
                   display: 'block', 
                   mt: 0.5, 
                   fontStyle: 'italic',
                   color: '#666'
                 }}
               >
                 ℹ️ User will receive an email invitation to set up their password. Password field is disabled for invited users.
               </Typography>
             )}
           </Box>
         </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              required
              label="First Name *"
              value={userForm.first_name}
              onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Middle Name"
              value={userForm.middle_name}
              onChange={(e) => setUserForm({ ...userForm, middle_name: e.target.value })}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              required
              label="Last Name *"
              value={userForm.last_name}
              onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={userForm.phone_number}
              onChange={(e) => setUserForm({ ...userForm, phone_number: e.target.value })}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Role *</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                label="Role *"
                disabled={editingUser?.is_primary_user}
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
              {editingUser?.is_primary_user && (
                <FormHelperText>Primary user role cannot be changed</FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth required>
              <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Company *</InputLabel>
              <Select
                value={userForm.company_id}
                onChange={(e) => setUserForm({ ...userForm, company_id: e.target.value })}
                label="Company *"
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.company_name} ({company.subscription_plan})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Company is required for PropTrak users</FormHelperText>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                     <Box sx={{ flex: 1 }}>
             <FormControl fullWidth>
               <InputLabel>Status</InputLabel>
               <Select
                 value={userForm.status}
                 onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                 label="Status"
               >
                 {statusOptions.map((status) => (
                   <MenuItem key={status.value} value={status.value}>
                     {status.label}
                   </MenuItem>
                 ))}
               </Select>
               <FormHelperText>
                 {userForm.status === 'invited' && 'User receives email invitation to set up password'}
                 {userForm.status === 'active' && 'User can immediately log in (requires password)'}
                 {userForm.status === 'suspended' && 'User account is temporarily disabled'}
               </FormHelperText>
             </FormControl>
           </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={userForm.timezone}
                onChange={(e) => setUserForm({ ...userForm, timezone: e.target.value })}
                label="Timezone"
              >
                {timezoneOptions.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={userForm.system_disclosure}
                  onChange={(e) => setUserForm({ ...userForm, system_disclosure: e.target.checked })}
                />
              }
              label="System Disclosure Accepted"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeUserDialog}>Cancel</Button>
        <Button 
          onClick={editingUser ? handleUpdateUser : handleCreateUser}
          variant="contained"
          color="primary"
          disabled={editingUser ? operationLoading[`update_${editingUser.id}`] : false}
          startIcon={editingUser && operationLoading[`update_${editingUser.id}`] ? (
            <CircularProgress size={16} />
          ) : undefined}
        >
          {editingUser ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading PropTrak users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon />
          PropTrak User Management
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Environment Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Environment:
            </Typography>
            <ToggleButtonGroup
              value={environment}
              exclusive
              onChange={(_, newEnvironment) => {
                if (newEnvironment) {
                  handleEnvironmentChange(newEnvironment);
                }
              }}
              size="small"
            >
                              <ToggleButton value="staging">Staging</ToggleButton>
                <ToggleButton value="production">Production</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openUserDialog()}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Environment Info */}
      {environmentInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Current Environment:</strong> {environmentInfo.environment} | 
            <strong>PropTrak API:</strong> {environmentInfo.proptrak_api_base} | 
            <strong>Dev Mode:</strong> {environmentInfo.dev_mode ? 'Yes' : 'No'}
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="primary">
              {users.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
          </CardContent>
        </StyledCard>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="success.main">
              {users.filter(u => u.status === 'active').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Users
            </Typography>
          </CardContent>
        </StyledCard>
                 <StyledCard>
           <CardContent>
             <Typography variant="h6" color="warning.main">
               {users.filter(u => u.status === 'pending').length}
             </Typography>
             <Typography variant="body2" color="text.secondary">
               Pending Users
             </Typography>
             <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
               Auto-applied after first login
             </Typography>
           </CardContent>
         </StyledCard>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="info.main">
              {companies.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Companies
            </Typography>
          </CardContent>
        </StyledCard>
      </Box>

      {/* Users Table */}
      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                PropTrak Users
              </Typography>
              {lastDataRefresh && (
                <Typography variant="caption" color="text.secondary">
                  Last refreshed: {lastDataRefresh.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              onClick={loadData}
              disabled={loading}
              startIcon={loading ? <RefreshIcon /> : <RefreshIcon />}
              sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Details</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell title="Last sign-in to the PropTrak PM (property manager) web app only; not tenant/vendor/delegate portals.">
                    Last PM portal login
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.first_name} {user.middle_name ? user.middle_name + ' ' : ''}{user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {user.email}
                        </Typography>
                        {user.phone_number && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            📞 {user.phone_number}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          🕐 {user.timezone || 'Not set'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.company_name || 'Unknown Company'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user.company_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RoleChip
                          label={roleOptions.find(r => r.value === user.role)?.label || user.role}
                          role={user.role}
                          size="small"
                        />
                        {user.is_primary_user && (
                          <Tooltip title="Primary User - Role cannot be changed">
                            <StarIcon sx={{ fontSize: 18, color: '#FFD700' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        status={user.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.last_pm_portal_login
                        ? new Date(user.last_pm_portal_login).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit User">
                          <IconButton 
                            size="small" 
                            onClick={() => openUserDialog(user)}
                            disabled={operationLoading[`update_${user.id}`]}
                          >
                            {operationLoading[`update_${user.id}`] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <EditIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        {user.status === 'active' ? (
                          <Tooltip title={user.is_primary_user ? "Primary user cannot be suspended" : "Suspend User"}>
                            <span>
                              <IconButton 
                                size="small" 
                                onClick={() => handleSuspendUser(user.id)}
                                disabled={operationLoading[`suspend_${user.id}`] || user.is_primary_user}
                              >
                                {operationLoading[`suspend_${user.id}`] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <BlockIcon color={user.is_primary_user ? "disabled" : "inherit"} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleActivateUser(user.id)}
                              disabled={operationLoading[`activate_${user.id}`]}
                            >
                              {operationLoading[`activate_${user.id}`] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckCircleIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete User">
                          <IconButton 
                            size="small" 
                            onClick={() => openDeleteConfirmDialog(user.id, `${user.first_name} ${user.last_name}`)}
                            disabled={operationLoading[`delete_${user.id}`]}
                          >
                            {operationLoading[`delete_${user.id}`] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>

      {/* Dialogs */}
      {renderUserDialog()}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, userId: null, userName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the PropTrak user <strong>{deleteConfirmDialog.userName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The user will be permanently removed from the PropTrak system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, userId: null, userName: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (deleteConfirmDialog.userId) {
                handleDeleteUser(deleteConfirmDialog.userId);
                setDeleteConfirmDialog({ open: false, userId: null, userName: '' });
              }
            }}
            color="error"
            variant="contained"
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropTrakUserManagement; 